import { html, LitElement, unsafeCSS } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { Vector } from "matter-js";
import { EntityManager } from "../core/EntityManager";
import { InputManager } from "../core/InputManager";
import { SimulationEngine } from "../core/SimulationEngine";
import styleBubble from '../entities-styles/_bubble.scss?inline'; // Estilos específicos para las burbujas
import styleCluster from '../entities-styles/_cluster.scss?inline'; // Estilos específicos para el cluster
import styleSelection from '../entities-styles/_selection-marquee.scss?inline'; // Estilos para el cuadro de selección
import { BubbleEntity } from "../entities/BubbleEntity";
import { ClusterEntity } from "../entities/ClusterEntity";
import { WallEntity } from "../entities/WallEntity";
import { BubbleClickEvent, BubbleSelectionChangeEvent } from "../utils/events";
import { computeBubbleRadius } from "../utils/math";
import { BubbleFieldStorage } from "../utils/storage";
import style from './BubbleField.scss?inline';

@customElement('app-bubble-field')
export class BubbleField extends LitElement {
    private engine: SimulationEngine;
    private entityManager: EntityManager;
    private inputManager: InputManager;
    private storage: BubbleFieldStorage;

    @property({ type: String }) k_storageKey: string = '';
    @query('.field-container') private container: HTMLElement;
    @query('.a11y-announcer') private announcer: HTMLElement;

    static styles = [
        unsafeCSS(style),
        unsafeCSS(styleBubble),
        unsafeCSS(styleCluster),
        unsafeCSS(styleSelection)
    ];

    // protected createRenderRoot(): HTMLElement | DocumentFragment {
    //     return this;
    // }

    firstUpdated() {
        const marquee = this.shadowRoot.querySelector('.selection-marquee') as HTMLElement;

        this.storage = new BubbleFieldStorage();

        // Iniciar Motor
        this.engine = new SimulationEngine();
        this.engine._Init(this.container);

        // Iniciar Inputs (Gestos, clicks, etc)
        this.entityManager = new EntityManager(this.engine, this.storage);
        this.inputManager = new InputManager(
            this.engine._engine,
            this.container,
            this.entityManager, // Inyección
            marquee,             // Inyección
            {
                onBubbleClick: (bubble) => this.dispatchBubbleClickEvent(bubble),
                onSelectionChange: (selectedBubbles) => this.dispatchSelectionChangeEvent(selectedBubbles),
                onDoubleClick: () => this._ReorganizeBubblesInGrid()
            }
        );

        this.engine._AfterUpdateCallback = () => this.afterUpdateCallback();


        // Arrancar simulación
        this.engine._Start();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.engine) {
            // limpiar recursos cuando el componente se remueve del DOM
            this.engine._Stop();
        }
        this.inputManager?._Destroy();
    }

    updated(changedProperties: Map<string, any>) {
        if (changedProperties.has('k_storageKey')) {
            this.configureStorage();
        }
    }

    render() {
        return html`
            <div class="field-container" style="position: relative; width: 100%; height: 100%;">
                <!-- las burbujas se agregan aquí -->
                <div class="selection-marquee" aria-hidden="true"></div>
            </div>
            <!-- Anuncia cambios de selección a lectores de pantalla sin alterar el layout visual -->
            <div class="a11y-announcer" aria-live="polite" aria-atomic="true"></div>
        `;
    }


    // ***************************************************************************
    // #region Utils


    /**
     * Callback que se ejecuta después de cada actualización del motor de física.
     * Sincroniza las posiciones de las burbujas con el almacenamiento local.
     * Nota: Para optimizar, limitamos la frecuencia de escritura al almacenamiento a 1 vez por segundo.
     * */
    private afterUpdateCallback() {
        const lastIntent = (this as any).____lastStorageIntent || 0;
        const now = Date.now();
        if (now - lastIntent < 1000) return; // Limitar a 1 intento por segundo
        (this as any).____lastStorageIntent = now;

        const storageKey = this.k_storageKey;
        if (!storageKey) return;

        // Obtener posiciones actuales de las burbujas
        const positions: { [key: string]: { x: number, y: number } } = {};
        const bubbles = this._GetBubbleEntities();
        bubbles.forEach(bubble => {
            if (bubble._isInCluster || bubble === this.inputManager._GetClickedBubbleEntity()) return; // Ignorar burbujas dentro de cluster o la burbuja actualmente clickeada (posiblemente en movimiento)
            positions[bubble._id] = {
                x: bubble._body.position.x,
                y: bubble._body.position.y
            };
        });

        this.storage._SetStorageData(positions);
    }

    private getMaxColumnsFromGrid(): number {
        const rightMargin = 0;
        const width = this.container ? this.container.clientWidth : window.innerWidth;
        const radius = this.getCurrentBubbleRadius();
        const maxColumns = Math.floor(width / (radius * 2 + 10)) - rightMargin;
        return maxColumns < 1 ? 1 : maxColumns;
    }

    private getBubblePositionInGrid(index: number): { x: number, y: number } {
        const maxColumns = this.getMaxColumnsFromGrid();
        const radius = this.getCurrentBubbleRadius();
        const row = Math.floor(index / maxColumns);
        const col = index % maxColumns;
        return {
            x: 50 + col * (radius * 2 + 10),
            y: 50 + row * (radius * 2 + 10)
        };
    }

    /**
     * Radio de burbuja acorde al ancho actual del contenedor: se achica en pantallas pequeñas
     * (celulares) y crece hasta el radio máximo en pantallas grandes.
     */
    private getCurrentBubbleRadius(): number {
        const width = this.container ? this.container.clientWidth : window.innerWidth;
        return computeBubbleRadius(width);
    }

    private configureStorage() {
        this.storage._StorageKey = this.k_storageKey;

        if (!this.k_storageKey) return;

        // inicializar localstorage, verificar si existe un timeout para limpiar posiciones antiguas al día siguiente
        const today = new Date().toLocaleDateString();
        const storedDate = this.storage._GetStorageDate();
        if (storedDate !== today) {
            this.storage._SetStorageData({});
            this.storage._SetStorageDate(today);
        }
    }

    /** Aplica estilos iniciales al contenido de una burbuja para que se ajuste correctamente */
    private applyStyleToBubble(bubbleContent: HTMLElement) {
        bubbleContent.style.width = `100%`;
        bubbleContent.style.height = `100%`;
    }

    /**
     * Crea una nueva burbuja a partir de un contenido dado,
     * asignándole una posición inicial en grilla o desde el almacenamiento.
     */
    private getNewBubble<ElementType extends HTMLElement = HTMLElement, DatumType = any>(
        bubbleConfig: IBubbleConfig<ElementType, DatumType>,
        index: number = this.engine._GetTotalEntities(BubbleEntity)
    ): BubbleEntity<DatumType> {
        const lastPositions = this.storage._LastPosition;
        const id = bubbleConfig.id || bubbleConfig.content.id;

        this.applyStyleToBubble(bubbleConfig.content);

        const pos = lastPositions?.[id] || this.getBubblePositionInGrid(index); // posición inicial
        const bubble = new BubbleEntity<DatumType>({
            id: `${id}`,
            datum: bubbleConfig.datum,
            x: pos.x,
            y: pos.y,
            radius: this.getCurrentBubbleRadius(),
            content: bubbleConfig.content,
            onSelectToggle: bubbleConfig.onSelectToggle as any,
            auraColor: bubbleConfig.auraColor,
            isSelectable: bubbleConfig.isSelectable !== false, // Por defecto, las burbujas son seleccionables
        });

        return bubble;
    }

    /**
     * Refresca una burbuja existente in-place con la nueva configuración recibida:
     * actualiza su contenido visual, datum, color de aura, selectabilidad y callback,
     * sin tocar su posición física ni su estado de selección/cluster actual.
     */
    private refreshBubble<ElementType extends HTMLElement = HTMLElement, DatumType = any>(
        bubble: BubbleEntity<DatumType>,
        bubbleConfig: IBubbleConfig<ElementType, DatumType>
    ) {
        if (bubbleConfig.content && bubbleConfig.content !== bubble._ContentElement) {
            this.applyStyleToBubble(bubbleConfig.content);
            bubble._UpdateContent(bubbleConfig.content);
        }

        bubble._SetDatum(bubbleConfig.datum);
        bubble._SetOnSelectToggle(bubbleConfig.onSelectToggle as any);
        bubble._AuraColor = bubbleConfig.auraColor;
        bubble._SetIsSelectable(bubbleConfig.isSelectable !== false);
    }

    // #endregion
    // ***************************************************************************

    // ***************************************************************************
    // #region EVENTS

    private dispatchBubbleClickEvent(bubble: BubbleEntity) {
        const event = new CustomEvent<BubbleClickEvent["detail"]>('bubble-click', {
            detail: { bubbleId: bubble._id, datum: bubble._datum },
            bubbles: true,
            composed: true
        });
        this.dispatchEvent(event);
    }

    private dispatchSelectionChangeEvent(selectedBubbles: BubbleEntity[]) {
        const ids = [];
        const data = [];
        for (const bubble of selectedBubbles) {
            ids.push(bubble._id);
            data.push(bubble._datum);
        }

        if (this.announcer) {
            this.announcer.textContent = ids.length > 0
                ? `${ids.length} burbuja${ids.length === 1 ? '' : 's'} seleccionada${ids.length === 1 ? '' : 's'}`
                : 'Selección vacía';
        }

        const event = new CustomEvent<BubbleSelectionChangeEvent["detail"]>('bubblefield-selection-change', {
            detail: { selectedBubbleIds: ids, data: data },
            bubbles: true,
            composed: true
        });
        this.dispatchEvent(event);
    }


    // #endregion
    // ***************************************************************************


    // ***************************************************************************
    // #region API


    public _GetBubbleEntities(): BubbleEntity[] {
        return [...this.engine._GetEntities()].filter(e => e instanceof BubbleEntity) as BubbleEntity[];
    }

    public _GetEntityById(id: string): BubbleEntity | ClusterEntity | WallEntity | undefined {
        return this.engine._GetEntityById(id) as BubbleEntity | ClusterEntity | WallEntity | undefined;
    }

    public _GetCurrentBubbleClicked(): BubbleEntity | null {
        return this.inputManager._GetClickedBubbleEntity() as BubbleEntity || null;
    }

    public _SelectBubbleById(id: string[], selected: boolean) {
        const bubbles = id.map(i => this.engine._GetEntityById(i)).filter(b => b !== undefined) as BubbleEntity[];
        bubbles.forEach(bubble => {
            if (bubble._isSelected === selected) return; // Evitar cambios innecesarios
            bubble._ToggleSelection(selected)
        });
    }

    /**
     * Crea un cluster a partir de las burbujas actualmente seleccionadas.
     * @param usePointerPositionAsCenter Si es true, el cluster se creará centrado en la posición del puntero;
     *      si es false, se usará la burbuja primaria o el centroide de las seleccionadas.
     *
     * Nota: Si no hay burbujas seleccionadas, no se crea ningún cluster.
     */
    public _CreateClusterFromSelection(usePointerPositionAsCenter: boolean = false) {
        // if (this.entityManager._GetCluster()) return;
        const selectedBubbles = this.entityManager._GetSelectedBubbles();
        if (selectedBubbles.length > 0) { // Solo creamos un cluster si hay burbujas seleccionadas
            const centerBubble = this.inputManager._GetClickedBubbleEntity() as BubbleEntity;
            const pointer = usePointerPositionAsCenter ? this.inputManager._GetPointerPosition() : undefined;
            this.inputManager._DisableMouseConstraint();
            this.entityManager._CreateClusterFromSelection(selectedBubbles, centerBubble, pointer);
        }
    }

    public _GetCluster(): ClusterEntity | null {
        return this.entityManager._GetCluster();
    }

    public _RemoveClusterIfExist() {
        this.inputManager._EnableMouseConstraint();
        this.entityManager._RemoveClusterIfExist();
    }

    /**
     * Mueve el cluster (si existe) a la posición especificada.
     * Si no hay cluster, no hace nada.
     * @param position Posición destino a la que se quiere mover el cluster.
     * @param instant Si es true, coloca el cluster exactamente en `position` sin transición
     * (usar solo justo al crear el cluster, para que aparezca ya junto al puntero).
     */
    public _MoveClusterTo(position: Vector, instant: boolean = false) {
        const cluster = this.entityManager._GetCluster();
        if (!cluster) return;

        if (instant) {
            cluster._CancelLaunch();
            cluster._SetPosition(position);
            return;
        }

        // Seguimiento con velocidad controlada (no teletransportado):
        // Al perseguir el vector suavemente evitamos tirones.
        cluster._LaunchTo(position.x, position.y, 0.3);
    }

    public _CancelLassoIfActive() {
        this.inputManager._CancelLassoIfActive();
    }

    /** Refresca las dimensiones del contenedor */
    public _RefreshDimensions() {
        this.engine._UpdateDimensions();
    }

    public _HasBubbleId(id: string): boolean {
        const bubble = this.engine._GetEntityById(id);
        return bubble instanceof BubbleEntity;
    }

    /**
     * Sincroniza el campo con la configuración recibida: las burbujas nuevas se crean,
     * y las que ya existen (mismo id) se refrescan in-place (contenido, datum, aura, etc.)
     * para reflejar los cambios visualmente sin perder su posición ni estado físico actual.
     */
    public _SetBubbles<ElementType extends HTMLElement = HTMLElement, DatumType = any>(bubblesConfig: IBubbleConfig<ElementType, DatumType>[]) {
        let nextGridIndex = this.engine._GetTotalEntities(BubbleEntity); // índice de grilla para las nuevas burbujas
        bubblesConfig.forEach((bubbleConfig) => {
            const id = bubbleConfig.id || bubbleConfig.content.id;
            const existingBubble = this.engine._GetEntityById(id);

            if (existingBubble instanceof BubbleEntity) {
                this.refreshBubble(existingBubble, bubbleConfig);
                return;
            }

            const bubble = this.getNewBubble(bubbleConfig, nextGridIndex++);
            this.engine._AddEntity(bubble); // Añadir al motor físico
        });
    }

    /**
     * Actualiza el contenido de una burbuja existente.
     */
    public _UpdateBubbleContent(id: string, newContent: HTMLElement) {
        const bubble = this.engine._GetEntityById(id);
        if (bubble && bubble instanceof BubbleEntity) {
            this.applyStyleToBubble(newContent);
            bubble._UpdateContent(newContent);
        }
    }

    public _RemoveBubblesById(ids: string[]) {
        ids.forEach(id => {
            const bubble = this.engine._GetEntityById(id);
            if (bubble && bubble instanceof BubbleEntity) {
                this.engine._RemoveEntity(id);
            }
        });
    }

    /** Reorganiza las burbujas en grilla */
    public _ReorganizeBubblesInGrid() {
        const bubbles = this._GetBubbleEntities();
        bubbles.forEach((bubble, i) => {
            const pos = this.getBubblePositionInGrid(i);
            // Colisiones deshabilitadas durante el viaje: evita que una burbuja en tránsito
            // empuje a otra ya ubicada (o también en tránsito) y desordene la grilla final.
            bubble._LaunchTo(pos.x, pos.y, 0.15, false);
        });
    }

    public _SetAuraColorToBubble(id: string[], color: string) {
        const bubbles = id.map(i => this.engine._GetEntityById(i)).filter(b => b !== undefined) as BubbleEntity[];
        bubbles.forEach(bubble => {
            bubble._AuraColor = color;
        });
    }

    public _HasWall(id: string): boolean {
        const wall = this.engine._GetEntityById(id);
        return wall instanceof WallEntity;
    }

    public _SetWalls(walls: { id: string, x: number, y: number, width: number, height: number }[]) {
        walls.forEach((wall) => {
            const wallEntity = new WallEntity({
                id: wall.id,
                x: wall.x,
                y: wall.y,
                width: wall.width,
                height: wall.height
            });
            this.engine._AddEntity(wallEntity);
        });
    }

    public _ClearWalls() {
        const walls = [...this.engine._GetEntities()].filter(e => e instanceof WallEntity) as WallEntity[];
        walls.forEach(wall => {
            this.engine._RemoveEntity(wall._id);
        });
    }

    public _GetPointerPosition(): { x: number, y: number } {
        const pos = this.inputManager._GetPointerPosition();
        return { x: pos.x, y: pos.y };
    }

    // #endregion
    // ***************************************************************************
}

export interface IBubbleConfig<ElementType extends HTMLElement = HTMLElement, DatumType = any> {
    id: string;
    content: ElementType;
    datum?: DatumType;
    /** @default true */
    isSelectable?: boolean;
    auraColor?: string;
    onSelectToggle?: (bubble: ElementType, selected: boolean) => void;
}
